import { useTranslation } from 'react-i18next'

import { LivePriceCell } from '@pages/Trade/Portfolio/components/LivePriceCell'

import { Body1, Body2, Title1 } from '@ui/Typography'
import { IconButton } from '@ui/components/Button'
import { Tabs, type Tab } from '@ui/components/Tabs'
import { CloseIcon } from '@ui/icons/Close.icon'

import { useWindowResize } from '@utils/domUtils'
import { getCurrencySymbol } from '@utils/transaction.utils'

import type { DxTradeSide } from '@model/DxTrade.types'
import type { CurrencyType } from '@model/WalletDto'

import { Box } from '@shared/components/Box'

import { getFavoriteIcon } from '../Markets/Instruments/helpers'

export enum OrderWidgetTab {
  Order = 'order',
  News = 'news',
  Trades = 'trades',
  Chart = 'chart',
  Market = 'market',
}

export type OrderWidgetVariant = 'order' | 'market'

interface WidgetTopProps {
  activeTab: OrderWidgetTab
  setActiveTab: (tab: OrderWidgetTab) => void
  asset: string
  description: string
  side: DxTradeSide
  instrumentCurrency?: CurrencyType
  priceIncrement?: number | null
  variant?: OrderWidgetVariant
  onClose: () => void
  isFavorite: boolean
  onToggleFavorite: () => void
  tradesCount?: number
}

export const WidgetTop = ({
  activeTab,
  setActiveTab,
  asset,
  description,
  side,
  instrumentCurrency,
  priceIncrement,
  variant = 'order',
  onClose,
  isFavorite,
  onToggleFavorite,
  tradesCount,
}: WidgetTopProps) => {
  const { t } = useTranslation()
  const isMobile = useWindowResize()
  const hasTradesTab = (tradesCount ?? 0) > 0
  const instrumentCurrencySymbol = instrumentCurrency
    ? `${getCurrencySymbol(instrumentCurrency)}`
    : undefined

  const isMarketVariant = variant === 'market'

  const tabs: Tab<OrderWidgetTab>[] = isMobile
    ? [
        { id: OrderWidgetTab.Chart, label: t('OrderWidget.Chart') },
        { id: OrderWidgetTab.News, label: t('OrderWidget.News') },
        ...(hasTradesTab
          ? [
              {
                id: OrderWidgetTab.Trades,
                label: `${t('OrderWidget.Trades')} (${tradesCount})`,
              },
            ]
          : []),
        { id: OrderWidgetTab.Market, label: t('OrderWidget.Market Info') },
      ]
    : [
        { id: OrderWidgetTab.Order, label: t('OrderWidget.Order') },
        { id: OrderWidgetTab.News, label: t('OrderWidget.News') },
        ...(hasTradesTab
          ? [
              {
                id: OrderWidgetTab.Trades,
                label: `${t('OrderWidget.Trades')} (${tradesCount})`,
              },
            ]
          : []),
        { id: OrderWidgetTab.Market, label: t('OrderWidget.Market Info') },
      ]
  const activeTabIndex = tabs.findIndex((t) => t.id === activeTab)
  const shouldShowTabs = !isMobile || isMarketVariant || activeTab !== OrderWidgetTab.Order
  const spacingProps = isMobile
    ? ({ pb: 'x16', pt: 'x16' } as const)
    : ({ pt: 'x16', pb: 'x16', px: 'x16' } as const)

  return (
    <Box {...spacingProps} flex direction='column' gap='x8'>
      <Box flex justify='space-between' gap='x8'>
        <Box flex direction='column' gap='x4'>
          <Box flex direction='row' gap='x8' align='center'>
            <Title1 medium color='text-high-em'>
              {asset}
            </Title1>
            <LivePriceCell
              trade={{ symbol: asset, side, priceIncrement }}
              priceType='order'
              TypographyComp={Body1}
              autoWidth
              prefix={instrumentCurrencySymbol}
            />
          </Box>
          <Body2 color='text-med-em'>{description}</Body2>
        </Box>

        <Box align='flex-start' flex gap='x8' justify='center'>
          <IconButton.Secondary
            aria-label={t('Instruments.Favorites')}
            size='sm'
            onClick={onToggleFavorite}
            icon={getFavoriteIcon(isFavorite)}
          />
          <IconButton.Secondary
            aria-label={t('Close')}
            size='sm'
            onClick={onClose}
            icon={CloseIcon}
          />
        </Box>
      </Box>

      {shouldShowTabs && (
        <Tabs
          key={activeTab}
          tabs={tabs}
          initialIndex={activeTabIndex === -1 ? 0 : activeTabIndex}
          noActive={activeTabIndex === -1}
          onTabClick={(t) => setActiveTab(t.id as OrderWidgetTab)}
        />
      )}
    </Box>
  )
}
